import { decodePotionDamage, potionLiquidColor, POTION_EFFECT_IDS } from '../services/potionBitfield';
import { spawnEggColors, SPAWN_EGG_ITEM_ID } from '../services/spawnEggColors';
import { itemSpriteSrc, tintedItemLayers } from '../services/itemSpriteLayers';

export interface Raster {
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
}

export interface IconLayer {
  src: string;
  /** Tried when `src` has no sprite, the way the panel shows a damaged tool with its undamaged icon. */
  fallback?: string;
  tint?: string;
}

export interface IconRecipe {
  layers: IconLayer[];
  enchanted: boolean;
}

export interface BakedIcon {
  blob: Blob;
  width: number;
  height: number;
  /** The sprite was a blown up texture; false for the isometric block renders, which are drawn at full size. */
  upscaled: boolean;
}

const BLOCK_TEXTURES = '/assets/textures/blocks';

export function blockTextureSrc(texture: string): string {
  return `${BLOCK_TEXTURES}/${texture}.png`;
}

/** Every texture name the copied resource pack holds, written next to them by the asset copy step. */
export async function loadBlockTextureNames(): Promise<string[]> {
  const response = await fetch(`${BLOCK_TEXTURES}/_manifest.json`);
  if (!response.ok) throw new Error(`Block texture manifest answered ${response.status}`);
  const files: string[] = await response.json();
  return files.filter((file) => file.endsWith('.png')).map((file) => file.slice(0, -'.png'.length));
}

/** No 1.8 texture is smaller, and a near empty overlay would otherwise read as blown up to any size. */
const SMALLEST_TEXTURE = 16;

/**
 * The panel's item sprites are 16 pixel textures blown up eight times, so one texture pixel is an
 * 8 by 8 block. Finding that block size takes the sprite back to the pixels the game draws.
 */
export function upscaleFactor(raster: Raster): number {
  for (const factor of [16, 8, 4, 2]) {
    if (raster.width % factor !== 0 || raster.height % factor !== 0) continue;
    if (raster.width / factor < SMALLEST_TEXTURE || raster.height / factor < SMALLEST_TEXTURE) continue;
    if (isBlockUniform(raster, factor)) return factor;
  }
  return 1;
}

function isBlockUniform(raster: Raster, factor: number): boolean {
  const pixels = new Uint32Array(raster.data.buffer, raster.data.byteOffset, raster.width * raster.height);
  for (let pixelY = 0; pixelY < raster.height; pixelY++) {
    const blockRow = (pixelY - (pixelY % factor)) * raster.width;
    for (let pixelX = 0; pixelX < raster.width; pixelX++) {
      if (pixels[pixelY * raster.width + pixelX] !== pixels[blockRow + pixelX - (pixelX % factor)]) return false;
    }
  }
  return true;
}

export function shrinkBy(raster: Raster, factor: number): Raster {
  if (factor === 1) return raster;
  const width = raster.width / factor;
  const height = raster.height / factor;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let pixelY = 0; pixelY < height; pixelY++) {
    for (let pixelX = 0; pixelX < width; pixelX++) {
      const from = (pixelY * factor * raster.width + pixelX * factor) * 4;
      data.set(raster.data.subarray(from, from + 4), (pixelY * width + pixelX) * 4);
    }
  }
  return { data, width, height };
}

/** Animated textures ship as a vertical strip of square frames; a still image is the first one. */
export function firstFrame(raster: Raster): Raster {
  if (raster.height <= raster.width || raster.height % raster.width !== 0) return raster;
  const size = raster.width * raster.width * 4;
  return { data: raster.data.slice(0, size), width: raster.width, height: raster.width };
}

function parseHex(colour: string): [number, number, number] {
  const value = Number.parseInt(colour.replace('#', ''), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** The game's tint multiplies every channel and leaves alpha alone, which is what keeps the shading. */
export function multiplyTint(raster: Raster, colour: string): void {
  const [red, green, blue] = parseHex(colour);
  const data = raster.data;
  for (let offset = 0; offset < data.length; offset += 4) {
    data[offset] = (data[offset] * red) / 255;
    data[offset + 1] = (data[offset + 1] * green) / 255;
    data[offset + 2] = (data[offset + 2] * blue) / 255;
  }
}

export function drawOver(target: Raster, layer: Raster): void {
  const out = target.data;
  const top = layer.data;
  for (let offset = 0; offset < out.length; offset += 4) {
    const topAlpha = top[offset + 3] / 255;
    if (topAlpha === 0) continue;
    const bottomAlpha = out[offset + 3] / 255;
    const alpha = topAlpha + bottomAlpha * (1 - topAlpha);
    for (let channel = 0; channel < 3; channel++) {
      out[offset + channel] =
        (top[offset + channel] * topAlpha + out[offset + channel] * bottomAlpha * (1 - topAlpha)) / alpha;
    }
    out[offset + 3] = alpha * 255;
  }
}

/** RenderItem.renderEffect draws the glint twice with vertex colour -8372020, turned -50 and 10 degrees. */
const GLINT_COLOUR = [0x80 / 255, 0x40 / 255, 0xcc / 255];
const GLINT_ANGLES = [-50, 10];

/**
 * How much of the glint texture one icon spans. The game samples it through the item atlas, whose size
 * depends on the loaded packs, so this follows the panel's own glint: a 64 pixel tile over a 40 pixel icon.
 */
const GLINT_SPAN = 40 / 64;

function sampleWrapped(glint: Raster, textureX: number, textureY: number, channel: number): number {
  const wrap = (value: number, size: number) => ((value % size) + size) % size;
  const left = Math.floor(textureX - 0.5);
  const top = Math.floor(textureY - 0.5);
  const fractionX = textureX - 0.5 - left;
  const fractionY = textureY - 0.5 - top;
  const at = (x: number, y: number) =>
    glint.data[(wrap(y, glint.height) * glint.width + wrap(x, glint.width)) * 4 + channel];
  const upper = at(left, top) * (1 - fractionX) + at(left + 1, top) * fractionX;
  const lower = at(left, top + 1) * (1 - fractionX) + at(left + 1, top + 1) * fractionX;
  return (upper * (1 - fractionY) + lower * fractionY) / 255;
}

/**
 * Bakes the glint as it stands at time zero. The game blends it with (GL_SRC_COLOR, GL_ONE), so each
 * pass adds its own colour squared, and only onto pixels the item covers.
 */
export function applyGlint(raster: Raster, glint: Raster): void {
  const data = raster.data;
  for (let pixelY = 0; pixelY < raster.height; pixelY++) {
    for (let pixelX = 0; pixelX < raster.width; pixelX++) {
      const offset = (pixelY * raster.width + pixelX) * 4;
      if (data[offset + 3] === 0) continue;
      const u = ((pixelX + 0.5) / raster.width) * GLINT_SPAN;
      const v = ((pixelY + 0.5) / raster.height) * GLINT_SPAN;
      for (const degrees of GLINT_ANGLES) {
        const radians = (degrees * Math.PI) / 180;
        const turnedU = u * Math.cos(radians) - v * Math.sin(radians);
        const turnedV = u * Math.sin(radians) + v * Math.cos(radians);
        for (let channel = 0; channel < 3; channel++) {
          const source =
            sampleWrapped(glint, turnedU * glint.width, turnedV * glint.height, channel) * GLINT_COLOUR[channel];
          data[offset + channel] += source * source * 255;
        }
      }
    }
  }
}

export async function fetchRaster(src: string): Promise<Raster | null> {
  const response = await fetch(src);
  if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) return null;
  const bitmap = await createImageBitmap(await response.blob());
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D is unavailable');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { data: image.data, width: image.width, height: image.height };
}

async function loadLayer(layer: IconLayer): Promise<{ raster: Raster; factor: number }> {
  const raster = (await fetchRaster(layer.src)) ?? (layer.fallback ? await fetchRaster(layer.fallback) : null);
  if (!raster) throw new Error(`No sprite at ${layer.src}`);
  const frame = firstFrame(raster);
  const factor = upscaleFactor(frame);
  const native = shrinkBy(frame, factor);
  if (layer.tint) multiplyTint(native, layer.tint);
  return { raster: native, factor };
}

let glintRaster: Promise<Raster | null> | null = null;

export async function bakeIcon(recipe: IconRecipe): Promise<BakedIcon> {
  const loaded = await Promise.all(recipe.layers.map(loadLayer));
  const layers = loaded.map((entry) => entry.raster);
  const { width, height } = layers[0];
  if (layers.some((layer) => layer.width !== width || layer.height !== height)) {
    throw new Error('Icon layers differ in size');
  }
  const icon: Raster = { data: new Uint8ClampedArray(width * height * 4), width, height };
  for (const layer of layers) drawOver(icon, layer);
  if (recipe.enchanted) {
    glintRaster ??= fetchRaster('/enchant_glint.png');
    const glint = await glintRaster;
    if (!glint) throw new Error('Glint texture is missing');
    applyGlint(icon, glint);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')?.putImageData(new ImageData(icon.data, width, height), 0, 0);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((encoded) => (encoded ? resolve(encoded) : reject(new Error('PNG encoding failed'))), 'image/png'),
  );
  return { blob, width, height, upscaled: loaded[0].factor > 1 };
}

/** The same layers `MinecraftItem` stacks for this item, so the inserted icon is the one in the picker. */
export function itemLayers(item: { id: number; name: string; metadata: number }): IconLayer[] {
  const identifier = `minecraft:${item.name}`;
  const tinted =
    item.id === SPAWN_EGG_ITEM_ID
      ? tintedItemLayers('minecraft:spawn_egg', undefined, spawnEggColors(item.metadata))
      : tintedItemLayers(identifier, potionLiquidColor(decodePotionDamage(item.metadata)));
  if (tinted) return tinted;
  return [{ src: itemSpriteSrc(identifier, item.metadata), fallback: itemSpriteSrc(identifier, 0) }];
}

export const POTION_SPLASH_BIT = 0x4000;

export interface PotionChoice {
  /** Null is the water bottle every brew starts from. */
  effectId: number | null;
  colour: string;
}

export function potionChoices(): PotionChoice[] {
  const water = decodePotionDamage(0);
  return [
    { effectId: null, colour: potionLiquidColor(water) },
    ...POTION_EFFECT_IDS.map((effectId) => ({
      effectId,
      colour: potionLiquidColor(water, [{ id: effectId, amplifier: 0, duration: 0 }]),
    })),
  ];
}

export function potionLayers(colour: string, splash: boolean): IconLayer[] {
  return tintedItemLayers(splash ? 'minecraft:splash_potion' : 'minecraft:potion', colour) ?? [];
}

export type DyeableKind = 'leather_helmet' | 'leather_chestplate' | 'leather_leggings' | 'leather_boots' | 'fireworks_charge';

export interface DyeableItem {
  kind: DyeableKind;
  /** Name in the item registry, for the tile and the layer name. */
  item: string;
  defaultColour: string;
}

export const DYEABLE_ITEMS: DyeableItem[] = [
  // ItemArmor.getColor answers 10511680 for leather nobody dyed.
  { kind: 'leather_helmet', item: 'leather_helmet', defaultColour: '#a06540' },
  { kind: 'leather_chestplate', item: 'leather_chestplate', defaultColour: '#a06540' },
  { kind: 'leather_leggings', item: 'leather_leggings', defaultColour: '#a06540' },
  { kind: 'leather_boots', item: 'leather_boots', defaultColour: '#a06540' },
  // ItemFireworkCharge tints its overlay 9079434 while the star carries no explosion.
  { kind: 'fireworks_charge', item: 'firework_charge', defaultColour: '#8a8a8a' },
];

/** Leather tints its body and leaves the overlay alone; a firework star does the opposite. */
export function dyeableLayers(kind: DyeableKind, colour: string): IconLayer[] {
  const body = `/mc/${kind}.png`;
  const overlay = `/mc/${kind}_overlay.png`;
  return kind === 'fireworks_charge'
    ? [{ src: body }, { src: overlay, tint: colour }]
    : [{ src: body, tint: colour }, { src: overlay }];
}

export interface Dye {
  /** Damage value of the dye item, which is also how its name is looked up. */
  metadata: number;
  /** ItemDye.dyeColors, what a firework star takes on. */
  firework: string;
  /** EntitySheep's fleece table times 255, what one dye gives undyed leather in RecipesArmorDyes. */
  leather: string;
}

export const DYES: Dye[] = [
  { metadata: 0, firework: '#1e1b1b', leather: '#191919' },
  { metadata: 1, firework: '#b3312c', leather: '#993333' },
  { metadata: 2, firework: '#3b511a', leather: '#667f33' },
  { metadata: 3, firework: '#51301a', leather: '#664c33' },
  { metadata: 4, firework: '#253192', leather: '#334cb2' },
  { metadata: 5, firework: '#7b2fbe', leather: '#7f3fb2' },
  { metadata: 6, firework: '#287697', leather: '#4c7f99' },
  { metadata: 7, firework: '#ababab', leather: '#999999' },
  { metadata: 8, firework: '#434343', leather: '#4c4c4c' },
  { metadata: 9, firework: '#d88198', leather: '#f27fa5' },
  { metadata: 10, firework: '#41cd34', leather: '#7fcc19' },
  { metadata: 11, firework: '#decf2a', leather: '#e5e533' },
  { metadata: 12, firework: '#6689d3', leather: '#6699d8' },
  { metadata: 13, firework: '#c354cd', leather: '#b24cd8' },
  { metadata: 14, firework: '#eb8844', leather: '#d87f33' },
  { metadata: 15, firework: '#f0f0f0', leather: '#ffffff' },
];

export type TintKind = 'grass' | 'foliage' | 'birch' | 'spruce' | 'lily' | 'stem' | 'redstone';

/** What each tint shows when nothing is picked, read off the 1.8.9 client. */
export const TINT_DEFAULTS: Record<TintKind, string> = {
  // BlockGrass.getRenderColor samples the grass colour map at temperature 0.5, rainfall 1.0.
  grass: '#7cbd6b',
  // ColorizerFoliage.getFoliageColorBasic, what the leaves item is drawn with.
  foliage: '#48b518',
  birch: '#80a755',
  spruce: '#619961',
  // BlockLilyPad.colorMultiplier in the world.
  lily: '#208030',
  // BlockStem at age 7: red age*32, green 255-age*8, blue age*4.
  stem: '#e0c71c',
  // BlockRedstoneWire at power 15.
  redstone: '#ff3300',
};

export interface BiomeTint {
  labelKey: string;
  grass: string;
  foliage: string;
}

/**
 * Sampled from the 1.8.9 grass and foliage colour maps at each biome's temperature and rainfall.
 * Swamp and mesa override the maps with fixed colours in their biome classes.
 */
export const BIOME_TINTS: BiomeTint[] = [
  { labelKey: 'MAPART_BIOME_PLAINS', grass: '#91bd59', foliage: '#77ab2f' },
  { labelKey: 'MAPART_BIOME_FOREST', grass: '#79c05a', foliage: '#59ae30' },
  { labelKey: 'MAPART_BIOME_JUNGLE', grass: '#59c93c', foliage: '#30bb0b' },
  { labelKey: 'MAPART_BIOME_TAIGA', grass: '#86b783', foliage: '#68a464' },
  { labelKey: 'MAPART_BIOME_SAVANNA', grass: '#bfb755', foliage: '#aea42a' },
  { labelKey: 'MAPART_BIOME_SWAMP', grass: '#6a7039', foliage: '#6a7039' },
  { labelKey: 'MAPART_BIOME_MESA', grass: '#90814d', foliage: '#9e814d' },
];

/** Block textures the game ships grey and colours at draw time, keyed by texture name. */
export const TINTED_TEXTURES: Record<string, { tint: TintKind; base?: string }> = {
  grass_top: { tint: 'grass' },
  // The side is plain dirt with a grey fringe laid over it, and only the fringe takes the tint.
  grass_side: { tint: 'grass', base: 'grass_side' },
  grass_side_overlay: { tint: 'grass' },
  tallgrass: { tint: 'grass' },
  fern: { tint: 'grass' },
  double_plant_grass_bottom: { tint: 'grass' },
  double_plant_grass_top: { tint: 'grass' },
  double_plant_fern_bottom: { tint: 'grass' },
  double_plant_fern_top: { tint: 'grass' },
  leaves_oak: { tint: 'foliage' },
  leaves_jungle: { tint: 'foliage' },
  leaves_acacia: { tint: 'foliage' },
  leaves_big_oak: { tint: 'foliage' },
  vine: { tint: 'foliage' },
  leaves_birch: { tint: 'birch' },
  leaves_spruce: { tint: 'spruce' },
  waterlily: { tint: 'lily' },
  melon_stem_connected: { tint: 'stem' },
  melon_stem_disconnected: { tint: 'stem' },
  pumpkin_stem_connected: { tint: 'stem' },
  pumpkin_stem_disconnected: { tint: 'stem' },
  redstone_dust_cross: { tint: 'redstone' },
  redstone_dust_line: { tint: 'redstone' },
};

export function blockTextureLayers(texture: string, tint: string | null): IconLayer[] {
  const tinted = TINTED_TEXTURES[texture];
  if (!tinted || !tint) return [{ src: blockTextureSrc(texture) }];
  if (tinted.base) {
    return [{ src: blockTextureSrc(tinted.base) }, { src: blockTextureSrc(`${tinted.base}_overlay`), tint }];
  }
  return [{ src: blockTextureSrc(texture), tint }];
}
